import type { AdjustmentState } from '../core/types';
import { clamp } from './clamp';
import { applyBrightness } from './brightness';
import { applyContrast } from './contrast';
import { toGrayscale } from './grayscale';
import { boxBlur } from './blur';
import { sharpen } from './sharpen';

/**
 * Run the full non-destructive processing pipeline.
 * Fuses brightness + contrast + grayscale in a single pixel pass,
 * then applies blur and sharpen as separate passes.
 */
export function runPipeline(source: ImageData, adjustments: AdjustmentState): ImageData {
  const { brightness, contrast, grayscale, blur, sharpen: sharpenAmount } = adjustments;

  const needsPixelPass = brightness !== 0 || contrast !== 0 || grayscale;
  const needsBlur = blur > 0;
  const needsSharpen = sharpenAmount > 0;

  // Fast path: no adjustments needed
  if (!needsPixelPass && !needsBlur && !needsSharpen) {
    return source;
  }

  let result: ImageData;

  // Fused pixel pass: brightness + contrast + grayscale
  if (needsPixelPass) {
    const { width, height, data: src } = source;
    result = new ImageData(width, height);
    const dst = result.data;
    const len = src.length;

    for (let i = 0; i < len; i += 4) {
      let r = src[i];
      let g = src[i + 1];
      let b = src[i + 2];

      // Brightness
      if (brightness !== 0) {
        r = applyBrightness(r, brightness);
        g = applyBrightness(g, brightness);
        b = applyBrightness(b, brightness);
      }

      // Contrast
      if (contrast !== 0) {
        r = applyContrast(r, contrast);
        g = applyContrast(g, contrast);
        b = applyContrast(b, contrast);
      }

      // Grayscale
      if (grayscale) {
        const gray = toGrayscale(r, g, b);
        r = g = b = gray;
      }

      dst[i] = clamp(Math.round(r), 0, 255);
      dst[i + 1] = clamp(Math.round(g), 0, 255);
      dst[i + 2] = clamp(Math.round(b), 0, 255);
      dst[i + 3] = src[i + 3];
    }
  } else {
    // Clone so downstream mutations don't affect source
    result = new ImageData(
      new Uint8ClampedArray(source.data),
      source.width,
      source.height,
    );
  }

  // Blur pass
  if (needsBlur) {
    result = boxBlur(result, blur);
  }

  // Sharpen pass
  if (needsSharpen) {
    result = sharpen(result, sharpenAmount);
  }

  return result;
}
